/**
 * gClient4CShadowObservation.ts — MER Algo Footprints — Phase 6 foundation (P1-Q4 / G-CLIENT-4C)
 *
 * Pure client-observation semantics for P1-Q4 position 9. Realizes exactly the
 * banked authority D-GCLIENT4C-1 through D-GCLIENT4C-3 and D-GCLIENT4C-10 (ADR
 * section 49), with the governance record in docs/MASTER_TRANSFORMATION_PHASE_PLAN.md.
 *
 * PURE MODULE BOUNDARY. No React, no Zustand, no fetch, no browser API, no timer,
 * no clock, no randomness, no persistence, no capability overlay, no provider, no
 * symbol, no actionability, no setup or risk logic. Every export is a constant or a
 * deterministic function of its arguments.
 *
 * The banked definition: the observed operand describes whether the
 * source-control-banked current client workflow treats the canonical
 * `(horizon, timeframe)` pair as operational without semantic substitution. It is
 * NOT user intent, capability support, the derived operand, provider availability,
 * actionability, or the success of any individual network request. Deriving it from
 * capability state would make the position-5 comparison circular and could suppress
 * legitimate mismatch evidence, so this module consumes only the canonical
 * vocabulary from the position-8 module and never its capability state.
 */

import {
  CanonicalClientHorizon,
  CanonicalClientTimeframe,
  isCanonicalHorizon,
  isCanonicalTimeframe,
} from './gClient1Capability';

// ── Banked V1 client-operational registry (D-GCLIENT4C-1) ───────────────────

/**
 * The exact six presently-served cells. This is source-control authority, not a
 * runtime-discovered set: it never expands because capability changes, a provider
 * responds, a fetch succeeds, or a timeframe becomes selectable. Changing it
 * requires a future explicit controlled gate.
 */
export const CURRENT_CLIENT_OPERATIONAL_PAIR_REGISTRY_V1 = [
  'scalp/1min',
  'scalp/5min',
  'scalp/15min',
  'intraday/1min',
  'intraday/5min',
  'intraday/15min',
] as const;

/** The exact three-field observation envelope. There is no fourth key. */
export interface ShadowObservation {
  readonly horizon: CanonicalClientHorizon;
  readonly timeframe: CanonicalClientTimeframe;
  readonly observedOperational: boolean;
}

// ── Pure derivation ─────────────────────────────────────────────────────────

/** Registry lookup key. Both operands come from fixed enums with no separator collision. */
export function observationPairKey(horizon: string, timeframe: string): string {
  return `${horizon}/${timeframe}`;
}

/**
 * The total boolean predicate (D-GCLIENT4C-3). Exactly the six registry cells are
 * `true`; all twenty-two remaining canonical cells are `false`. There is no null,
 * tri-state, placeholder, capability lookup, network dependency, or chart
 * dependency, and no default unrelated to the registry.
 */
export function deriveObservedOperational(
  horizon: CanonicalClientHorizon,
  timeframe: CanonicalClientTimeframe,
): boolean {
  const key = observationPairKey(horizon, timeframe);
  return (CURRENT_CLIENT_OPERATIONAL_PAIR_REGISTRY_V1 as readonly string[]).indexOf(key) !== -1;
}

/**
 * Builds the exact observation for a canonical pair, or `null` when the pair is not
 * canonical. A non-canonical input is never aliased, case-repaired, nearest-matched,
 * defaulted to `5min`, or substituted from an active-symbol interval — it simply
 * yields no observation and therefore no dispatch eligibility.
 */
export function buildShadowObservation(
  horizon: unknown,
  timeframe: unknown,
): ShadowObservation | null {
  if (!isCanonicalHorizon(horizon) || !isCanonicalTimeframe(timeframe)) return null;
  return Object.freeze({
    horizon,
    timeframe,
    observedOperational: deriveObservedOperational(horizon, timeframe),
  });
}

/**
 * The lifecycle dedupe identity, derived from exactly the three observation fields.
 * Authentication, capability state, symbol, provider, response, and time are
 * deliberately excluded. The encoding is collision-free over the canonical domain
 * because both vocabularies are fixed and the boolean is rendered as a fixed token.
 */
export function observationKey(observation: ShadowObservation): string {
  return `${observation.horizon}/${observation.timeframe}/${observation.observedOperational ? 'true' : 'false'}`;
}
